/**
 * The platform console has its own address (console.workright.in), apart
 * from the customer app (app.workright.in). Both serve this same SPA:
 * `/superadmin/*` opened on the app moves to the console, and customer
 * pages opened on the console move to the app. The backend answers console
 * API calls only from the console address (middleware.ConsoleOnly).
 *
 * VITE_CONSOLE_URL / VITE_APP_URL are baked in at build time; when either
 * is missing (local dev, CI) nothing is ever redirected.
 */

export interface Hosts {
  console: string;
  app: string;
}

// Pages that belong to a customer workspace (never the console).
const APP_ONLY = ["/app/", "/join/", "/select-workspace"];

// The router matches paths in any case, so these checks do too.
function under(pathname: string, prefix: string): boolean {
  const p = pathname.toLowerCase();
  return prefix.endsWith("/") ? p.startsWith(prefix) : p === prefix || p.startsWith(prefix + "/");
}

/** The address to move to, or null to stay. Pure. */
export function crossHostTarget(origin: string, pathname: string, search: string, hosts: Hosts): string | null {
  if (!hosts.console || !hosts.app) return null;
  if (origin === hosts.app && under(pathname, "/superadmin")) {
    return hosts.console + pathname + search;
  }
  if (origin === hosts.console && APP_ONLY.some((p) => under(pathname, p))) {
    return hosts.app + pathname + search;
  }
  return null;
}

/**
 * Where to go when the backend answers console_only (a super_admin or
 * support session used from the app, e.g. right after signing in there).
 * Customer-only pages would bounce straight back, so they land on the
 * console home. Null on the console itself, so it can never loop. Pure.
 */
export function consoleOnlyTarget(origin: string, pathname: string, search: string, hosts: Hosts): string | null {
  if (!hosts.console || !hosts.app || origin === hosts.console) return null;
  if (APP_ONLY.some((p) => under(pathname, p))) return hosts.console + "/";
  return hosts.console + pathname + search;
}

export function builtInHosts(): Hosts {
  const trim = (v: string | undefined) => (v ?? "").trim().replace(/\/+$/, "");
  return { console: trim(import.meta.env.VITE_CONSOLE_URL), app: trim(import.meta.env.VITE_APP_URL) };
}
